import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppContext } from "../../../context/appContext";
import { AlertModalCheckIcon } from "../../../assets";
import { AppLayout } from "../../../containers/layout/app";
import { AccessControlWrapper } from "./styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { AccessControlType } from "../../../types/app.type";
import Cookies from "universal-cookie";
import { retrieveAccessControlByIdService } from "../../../utils/api/usermanagement/accesscontrol/retrieveAccessControlById";
import { Box, CircularProgress, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { retrieveAllRolesService } from "../../../utils/api/usermanagement/role/retrieveAllRoles";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AccessControlMenu } from "../../../components/menu/accesscontrol";
import { retrieveAllSystemPermissionsService } from "../../../utils/api/usermanagement/accesscontrol/retrieveAllSystemPermissions";
import { BaseButton } from "../../../components/button/styled";
import { updateAccessControlService } from "../../../utils/api/usermanagement/accesscontrol/updateAccessControl";
import { BaseAlertModal } from "../../../components/modal/alert";

export const AccessControl = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const { setActiveNavItem } = useContext(AppContext);
    const initialAccessControl = useMemo(() => [
        { module: "Accounting", rights: [] },
        { module: "Approval", rights: [] },
        { module: "Configuration", rights: [] },
        { module: "Customer Service", rights: [] },
        { module: "Dashboard", rights: [] },
        { module: "FTD Management", rights: [] },
        { module: "Loan Management", rights: [] },
        { module: "Report", rights: [] },
        { module: "Teller Management", rights: [] },
        { module: "Transactions", rights: [] },
        { module: "User Management", rights: [] },
    ], []);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [activeModule, setActiveModule] = useState<string | null>(null);
    const [accessControl, setAccessControl] = useState<AccessControlType[]>(initialAccessControl);
    const [selectedAccessControlId, setSelectedAccessControlId] = useState<string | null>(" ");

    useEffect(() => {
        setActiveNavItem("User Management");
    }, [setActiveNavItem]);

    const { data: roles } = useQuery({
        queryKey: ['roles', TOKEN],
        queryFn: async () => {
            const allRoles = await retrieveAllRolesService(TOKEN);
            return allRoles.data;
        },
        enabled: !!TOKEN,
    });

    const { data: systemPermissions } = useQuery({
        queryKey: ['systemPermissions', TOKEN],
        queryFn: async () => retrieveAllSystemPermissionsService(TOKEN),
        enabled: !!TOKEN,
    });

    const fetchAccessControl = useCallback(
        async (id: string) => {
            if (!id.trim()) {
                return setAccessControl(initialAccessControl);
            };
            try {
                const data = await retrieveAccessControlByIdService(TOKEN, id);
                // Transform the data to extract only the title values
                const transformedPermissions = data.permissions.map((permission: Record<string, any>) => ({
                    module: permission.module,
                    rights: permission.rights.map((right: Record<string, any>) => right.title)
                }));
                setAccessControl(transformedPermissions.length > 0 ? transformedPermissions : initialAccessControl);
            } catch (err) {
                console.error("Failed to fetch access control:", err);
            }
        },
        [TOKEN, initialAccessControl]
    );

    const handleRoleChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { value } = e.target;
        setError(null);
        await fetchAccessControl(value as string);
        return setSelectedAccessControlId(value as string);
    };

    const handleCheckboxChange = (right: string) => {
        setAccessControl(previous =>
            previous.map(permission => {
                if (permission.module === activeModule) {
                    const rightExists = permission.rights.includes(right);
                    return {
                        ...permission,
                        rights: rightExists
                            ? permission.rights.filter((r: string) => r !== right) // Remove if exists
                            : [...permission.rights, right] // Add if not exists
                    };
                }
                return permission; // Keep other modules unchanged
            })
        );
    };

    const handleActiveModuleChange = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, permission: AccessControlType) => {
        e.preventDefault();
        if (permission.module === activeModule) {
            return setActiveModule(null);
        }
        return setActiveModule(permission.module);
    };

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        return setIsAlertModalOpen(false);
    };

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        if (!selectedAccessControlId || !selectedAccessControlId.trim()) {
            setIsLoading(false);
            return setError("Please select a role");
        };
        // Transform the data to back to an object of title and right as
        // key/value pairs
        const transformedPermissions = accessControl.map(permission => ({
            module: permission.module,
            rights: permission.rights.map((right: string) => ({ title: right })),
        }));
        const payload = {
            permissions: transformedPermissions,
        };
        try {
            const response = await updateAccessControlService(TOKEN, selectedAccessControlId, payload);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Access Control Update failed. Please try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Error: ${error.message}`);
            console.error('Error:', error);
        }
    };

    return (
        <AppLayout
            pageId={"Access-Control"}
            pageTitle={"Access Control"}
        >
            <AccessControlWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Access Control Successfully Updated"
                    message="Your information has been successfully updated."
                    callToAction="Close"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box
                    component={"div"}
                    className="role-form-box"
                >
                    <Typography
                        variant="h2"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--dark-color)"
                        marginBlockEnd={"calc(var(--basic-margin)/4)"}
                    >
                        Select Role
                    </Typography>
                    <form>
                        <BaseFieldSet>
                            <BaseSelect
                                fullWidth
                                name="role"
                                fontweight={400}
                                colour="var(--semi-gray-color)"
                                value={selectedAccessControlId}
                                onChange={(e) => handleRoleChange(e)}
                            >
                                <BaseOption
                                    value={" "}
                                >
                                    Select role
                                </BaseOption>
                                {roles?.map((role: Record<any, any>, index: string) => {
                                    return (
                                        <BaseOption
                                            key={index}
                                            value={role.accessControl}
                                        >
                                            {role.title}
                                        </BaseOption>
                                    )
                                })}
                            </BaseSelect>
                        </BaseFieldSet>
                    </form>
                </Box>
                <Box
                    component={"div"}
                    className="access-control-box"
                >
                    <Box>
                        <Typography
                            variant="h2"
                            fontFamily={"Poppins"}
                            fontWeight={500}
                            fontSize={18}
                            lineHeight={"normal"}
                            color="var(--dark-color)"
                            marginBlockEnd={"var(--basic-margin)"}
                        >
                            Select User Access
                        </Typography>
                    </Box>
                    <Stack
                        gap={"var(--flex-gap)"}
                    >
                        {accessControl.map((permission, index) => {
                            return (
                                <Box
                                    key={index}
                                >
                                    <Stack
                                        className="module-title"
                                        direction={"row"}
                                        gap={"var(--flex-gap)"}
                                        justifyContent={"space-between"}
                                        onClick={(e) => handleActiveModuleChange(e, permission)}
                                    >
                                        <Box
                                            overflow={"hidden"}
                                        >
                                            <Typography
                                                variant="h3"
                                                fontFamily={"Poppins"}
                                                fontWeight={500}
                                                fontSize={16}
                                                lineHeight={"normal"}
                                                color="var(--light-color)"
                                            >
                                                {permission.module}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <KeyboardArrowDownIcon
                                                sx={{
                                                    color: "var(--light-color)"
                                                }}
                                            />
                                        </Box>
                                    </Stack>
                                    <AccessControlMenu
                                        open={permission.module === activeModule}
                                        systemrights={systemPermissions
                                            ?.filter((permission: Record<string, any>) => permission.module === activeModule)
                                            .flatMap((permission: Record<string, any>) => permission?.rights?.map((right: Record<string, any>) => right.title))
                                        }
                                        assignedrights={accessControl
                                            .filter((permission: Record<string, any>) => permission.module === activeModule)
                                            .flatMap((permission: Record<string, any>) => permission?.rights?.map((right: Record<string, any>) => right))}
                                        handleCheckboxChange={handleCheckboxChange}
                                    />
                                </Box>
                            )
                        })}
                    </Stack>
                </Box>
                {error && <Typography
                    fontFamily={"Inter"}
                    fontWeight={"600"}
                    fontSize={14}
                    lineHeight={"normal"}
                    color={"var(--text-salmon-pink-color)"}
                    whiteSpace={"normal"}
                >
                    {error}
                </Typography>}
                <Box
                    overflow={"hidden"}
                >
                    <BaseButton
                        type="submit"
                        variant="contained"
                        bgcolor="var(--primary-color)"
                        disableElevation
                        sx={{
                            width: { mobile: "100%", miniTablet: "auto" }
                        }}
                        onClick={handleSubmit}
                    >
                        {isLoading ? (
                            <CircularProgress color="inherit" className="loader" />
                        ) : (
                            <Typography
                                variant={"button"}
                                fontFamily={"inherit"}
                                fontWeight={"inherit"}
                                fontSize={"inherit"}
                                lineHeight={"inherit"}
                                color={"inherit"}
                                textTransform={"inherit"}
                            >
                                Submit
                            </Typography>
                        )}
                    </BaseButton>
                </Box>
            </AccessControlWrapper>
        </AppLayout >
    )
}